import React, { Component } from 'react';
import CanvasElement, { CanvasElementPropTypes } from '../../../canvas-element';
import pick from 'lodash/pick';
import SelectedElementWrapperUtil from '../utils';
import { observer } from 'mobx-react';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ServicesContext } from '../../context';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import type { SelectElementWrapper } from '@src/framework/editor/src/page/index/components/canvas/edit-panel/selected-element-layer/selected-ele-wrapper-factory';
import { isContextMenuEvent, isMetaOrShiftOrCtrlKey } from '@src/utils/key-mouse-event/mouse-event-check';

interface SelectedTableWrapperProps extends SelectElementWrapper {
    element: ITableModel;
    scale: number;
    isSelected?: boolean;
    selectable?: boolean;
    canBeConnected?: boolean;
    showSelectBorder?: boolean;
    borderDraggable?: boolean;
    canRotate?: boolean;
    isControlValuesChanging?: boolean;
    resizeVertical?: boolean;
    resizeHorizontal?: boolean;
    draggable?: boolean;
}

@observer
class SelectedTableWrapper extends Component<SelectedTableWrapperProps> {
    public static contextType = ServicesContext;
    private needMouseUp: boolean;

    public constructor(props) {
        super(props);
    }

    public render() {
        const elementModel = this.props.element;
        const frameInfo = SelectedElementWrapperUtil.frameInfoForInner(elementModel);
        const relativeInfo = SelectedElementWrapperUtil.relativeInfoForInner(elementModel);

        return (
            <CanvasElement
                {...pick(this.props, Object.keys(CanvasElementPropTypes))}
                scale={this.props.scale}
                rect={frameInfo}
                canRotate={false}
                relativeRotate={relativeInfo.relativeRotate}
                relativeFlipH={relativeInfo.relativeFlipH}
                relativeFlipV={relativeInfo.relativeFlipV}
                rotate={0}
                {
                    ...SelectedElementWrapperUtil
                        .commonPropsForCanvasElement(elementModel, this.props.scale)
                }
                onResizeStart={this.handleResizeStart}
                onBorderMouseDown={this.handleTableBorderMouseDown}
                onBorderMouseUp={this.handleTableBorderMouseUp}
                onBorderClick={this.props.onBorderClick}
                onBorderDoubleClick={this.handleBorderDoubleClick}
            >
            </CanvasElement>
        );
    }

    public componentDidMount() {
        // eslint-disable-next-line max-len
        if (window.event && isMetaOrShiftOrCtrlKey(window.event as MouseEvent) && !isContextMenuEvent(window.event as MouseEvent)) {
            this.props.element.uploadEditingCellData(true);
            this.props.element.showSelection = false;
            this.props.element.afterSelection(
                0, 0,
                this.props.element.props.rowHeights.length - 1,
                this.props.element.props.colWidths.length - 1,
            );
        } else if (this.props.element.selectedRange === null) {
            this.props.element.afterSelection(
                0, 0,
                this.props.element.props.rowHeights.length - 1,
                this.props.element.props.colWidths.length - 1,
            );
        }

        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.addChangesetObserver(this.updateComponent);
        }
    }

    public updateComponent = () => {
        this.forceUpdate();
    };

    public componentWillUnmount() {
        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.removeChangesetObserver(this.updateComponent);
        }
        // 失去焦点
        this.props.element.uploadEditingCellData(true);
        this.props.element.showSelection = false;
        this.props.element.selectedCellRanges = [];
        this.props.element.selectedRange = null;
    }

    private getSelectionService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }

    private getResizeElementHandler() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.resizeElementHandler;
    }

    private handleResizeStart = (e, resizeMode) => {
        // 单元格失焦上传数据，选中整个表格
        const elementModel = this.props.element;
        elementModel.uploadEditingCellData(true);
        elementModel.showSelection = false;
        elementModel.afterSelection(
            0,
            0,
            elementModel.props.rowHeights.length - 1,
            elementModel.props.colWidths.length - 1,
        );
        this.getResizeElementHandler()?.handleResizeStart(elementModel, e, this.props.scale, resizeMode);
    };

    private handleTableBorderMouseDown = (e) => {
        this.props.element.uploadEditingCellData(true);
        if (e && (e.metaKey || e.shiftKey || e.ctrlKey)) {
            this.needMouseUp = true;
        } else {
            this.props.element.showSelection = false;
            this.props.element.afterSelection(
                0, 0,
                this.props.element.props.rowHeights.length - 1,
                this.props.element.props.colWidths.length - 1,
            );
        }
    };

    private handleTableBorderMouseUp = () => {
        (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .editableServiceLayer?.selectedAnims?.clear();
        if (this.needMouseUp === true) {
            if (this.props.element.selected) {
                this.getSelectionService()?.selectedElements.remove(this.props.element);
            }
            this.needMouseUp = false;
        }
    };

    private handleBorderDoubleClick = (e) => {
        e?.stopPropagation();
    };
}

export default SelectedTableWrapper;
